import { Box, Button, ButtonGroup, Card, Checkbox, FormControl, FormControlLabel, FormLabel, Stack, TextField, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PageHeader } from '../../app/AppStyles';
import { fetchTimetableComplete, postTimetableMoments, resetTimetableMomentManagementStatus } from './timetableManagementSlice';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    width: { xs: '90%', md: '50%' },
    maxHeight: '90%',
    boxShadow: 24,
};

const defaultMoment = {
    timetableId: "",
    remark: "",
    startDay: null,
    endDay: null,
    amount: 0,
    wholeDay: true,
    startTime: null,
    endTime: null,
    daysOfTheWeek: [],
    weekInterval: 1
};

export default function TimetableMomentAddCard({ handleClose, timetableId }) {
    const dispatch = useDispatch();

    const { user } = useSelector((state) => state.user);
    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { timetableMomentsPost } = useSelector((state) => state.management.timetable);

    // Status callback
    useEffect(() => {
        if (timetableMomentsPost.status === "success") {
            dispatch(resetTimetableMomentManagementStatus());
            dispatch(fetchTimetableComplete(timetableId));
            handleClose();
        } else if (timetableMomentsPost.status === "failed") {
            dispatch(resetTimetableMomentManagementStatus());
        };
    }, [dispatch, handleClose, timetableMomentsPost.status]);

    // Property states
    const [momentsData, setMomentsData] = useState(defaultMoment);
    const [changed, setChanged] = useState(false);
    const [completeCheck, setCompleteCheck] = useState(false);

    // Keep track of changed state 
    useEffect(() => {
        if (momentsData !== defaultMoment) {
            setChanged(true);
        } else {
            setChanged(false);
        }
    }, [momentsData, defaultMoment]);

    // Keep track of completed state 
    useEffect(() => {
        if (
            momentsData.startDay !== null &&
            momentsData.endDay !== null &&
            (momentsData.startTime !== null || momentsData.wholeDay) &&
            (momentsData.endTime !== null || momentsData.wholeDay)
        ) {
            setCompleteCheck(true);
        } else {
            setCompleteCheck(false);
        }
    }, [momentsData]);

    // Add click function (check property character limits etc.)
    const addClick = () => {
        let tempObject = { ...momentsData }; // Needed to circumvent weird read only property error

        if (!tempObject.wholeDay) {
            tempObject.startTime = tempObject.startTime.format("HH:mm:ss");
            tempObject.endTime = tempObject.endTime.format("HH:mm:ss");
        } else {
            tempObject.startTime = "00:00:00";
            tempObject.endTime = "00:00:00";
        }

        tempObject.timetableId = timetableId;
        tempObject.startDay = tempObject.startDay.format("YYYY-MM-DD");
        tempObject.endDay = tempObject.endDay.format("YYYY-MM-DD");
        tempObject.createdBy = `AP/${user.number}`;

        dispatch(postTimetableMoments(tempObject));
    };

    return (
        <Box sx={{ ...modalStyle }}>
            <Card sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', p: 2 }}>
                <Stack spacing={1} sx={{ flex: '1 1 auto' }}>
                    <Typography variant="h6" sx={{ ...PageHeader }}>{resourceFinal[appLanguage].words.TIMETABLE}{resourceFinal[appLanguage].words.MOMENT.toLowerCase()} {resourceFinal[appLanguage].words.ADD}</Typography>
                    <TextField
                        fullWidth
                        label={resourceFinal[appLanguage].words.REMARK}
                        onChange={(event) => setMomentsData({
                            ...momentsData,
                            remark: event.target.value
                        })}
                        variant="outlined"
                    />
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nl">
                        <DatePicker
                            slotProps={{ textField: { fullWidth: true } }}
                            label={`${resourceFinal[appLanguage].words.START}${resourceFinal[appLanguage].words.DAY.toLowerCase()} *`}
                            onChange={(value) => setMomentsData({
                                ...momentsData,
                                startDay: dayjs(value)
                            })}
                        />
                    </LocalizationProvider>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nl">
                        <DatePicker
                            slotProps={{ textField: { fullWidth: true } }}
                            label={`${resourceFinal[appLanguage].words.END}${resourceFinal[appLanguage].words.DAY.toLowerCase()} *`}
                            onChange={(value) => setMomentsData({
                                ...momentsData,
                                endDay: dayjs(value)
                            })}
                        />
                    </LocalizationProvider>
                    <FormControlLabel
                        label={`${resourceFinal[appLanguage].words.WHOLE} ${resourceFinal[appLanguage].words.DAY.toLowerCase()}`}
                        control={<Checkbox
                            checked={momentsData.wholeDay}
                            onChange={(event) => setMomentsData({
                                ...momentsData,
                                wholeDay: event.target.checked ? true : false
                            })}
                        />}
                    />
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nl">
                        <TimePicker
                            slotProps={{ textField: { fullWidth: true } }}
                            disabled={momentsData.wholeDay}
                            label={`${resourceFinal[appLanguage].words.START}${resourceFinal[appLanguage].words.TIME.toLowerCase()} *`}
                            onChange={(value) => setMomentsData({
                                ...momentsData,
                                startTime: dayjs(value)
                            })}
                        />
                    </LocalizationProvider>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nl">
                        <TimePicker
                            slotProps={{ textField: { fullWidth: true } }}
                            disabled={momentsData.wholeDay}
                            label={`${resourceFinal[appLanguage].words.END}${resourceFinal[appLanguage].words.TIME.toLowerCase()} *`}
                            onChange={(value) => setMomentsData({
                                ...momentsData,
                                endTime: dayjs(value)
                            })}
                        />
                    </LocalizationProvider>
                    <FormControl>
                        <FormLabel component="legend">{resourceFinal[appLanguage].words.DAYS}</FormLabel>
                        <Box>
                            <FormControlLabel
                                label={resourceFinal[appLanguage].words.DAY_MONDAY_SHORT}
                                labelPlacement="top"
                                control={<Checkbox
                                    checked={momentsData.daysOfTheWeek.includes(1)}
                                    onChange={(event) => setMomentsData({
                                        ...momentsData,
                                        daysOfTheWeek: event.target.checked
                                            ? [...momentsData.daysOfTheWeek, 1]
                                            : momentsData.daysOfTheWeek.filter(item => item !== 1)
                                    })}
                                />}
                            />
                            <FormControlLabel
                                label={resourceFinal[appLanguage].words.DAY_TUESDAY_SHORT}
                                labelPlacement="top"
                                control={<Checkbox
                                    checked={momentsData.daysOfTheWeek.includes(2)}
                                    onChange={(event) => setMomentsData({
                                        ...momentsData,
                                        daysOfTheWeek: event.target.checked
                                            ? [...momentsData.daysOfTheWeek, 2]
                                            : momentsData.daysOfTheWeek.filter(item => item !== 2)
                                    })}
                                />}
                            />
                            <FormControlLabel
                                label={resourceFinal[appLanguage].words.DAY_WEDNESDAY_SHORT}
                                labelPlacement="top"
                                control={<Checkbox
                                    checked={momentsData.daysOfTheWeek.includes(3)}
                                    onChange={(event) => setMomentsData({
                                        ...momentsData,
                                        daysOfTheWeek: event.target.checked
                                            ? [...momentsData.daysOfTheWeek, 3]
                                            : momentsData.daysOfTheWeek.filter(item => item !== 3)
                                    })}
                                />}
                            />
                            <FormControlLabel
                                label={resourceFinal[appLanguage].words.DAY_THURSDAY_SHORT}
                                labelPlacement="top"
                                control={<Checkbox
                                    checked={momentsData.daysOfTheWeek.includes(4)}
                                    onChange={(event) => setMomentsData({
                                        ...momentsData,
                                        daysOfTheWeek: event.target.checked
                                            ? [...momentsData.daysOfTheWeek, 4]
                                            : momentsData.daysOfTheWeek.filter(item => item !== 4)
                                    })}
                                />}
                            />
                            <FormControlLabel
                                label={resourceFinal[appLanguage].words.DAY_FRIDAY_SHORT}
                                labelPlacement="top"
                                control={<Checkbox
                                    checked={momentsData.daysOfTheWeek.includes(5)}
                                    onChange={(event) => setMomentsData({
                                        ...momentsData,
                                        daysOfTheWeek: event.target.checked
                                            ? [...momentsData.daysOfTheWeek, 5]
                                            : momentsData.daysOfTheWeek.filter(item => item !== 5)
                                    })}
                                />}
                            />
                            <FormControlLabel
                                label={resourceFinal[appLanguage].words.DAY_SATURDAY_SHORT}
                                labelPlacement="top"
                                control={<Checkbox
                                    checked={momentsData.daysOfTheWeek.includes(6)}
                                    onChange={(event) => setMomentsData({
                                        ...momentsData,
                                        daysOfTheWeek: event.target.checked
                                            ? [...momentsData.daysOfTheWeek, 6]
                                            : momentsData.daysOfTheWeek.filter(item => item !== 6)
                                    })}
                                />}
                            />
                            <FormControlLabel
                                label={resourceFinal[appLanguage].words.DAY_SUNDAY_SHORT}
                                labelPlacement="top"
                                control={<Checkbox
                                    checked={momentsData.daysOfTheWeek.includes(7)}
                                    onChange={(event) => setMomentsData({
                                        ...momentsData,
                                        daysOfTheWeek: event.target.checked
                                            ? [...momentsData.daysOfTheWeek, 7]
                                            : momentsData.daysOfTheWeek.filter(item => item !== 7)
                                    })}
                                />}
                            />
                        </Box>
                    </FormControl>
                    <TextField
                        fullWidth
                        type="number"
                        value={momentsData.weekInterval}
                        label={`${resourceFinal[appLanguage].words.WEEK}${resourceFinal[appLanguage].words.INTERVAL.toLowerCase()}`}
                        onChange={(value) => setMomentsData({
                            ...momentsData,
                            weekInterval: value
                        })}
                        InputProps={{
                            inputProps: { min: 1 }
                        }}
                        variant="outlined"
                    />
                    {timetableMomentsPost.data === false && <>
                        <Typography variant="body1" color="error" sx={{ textAlign: 'center' }}>{resourceFinal[appLanguage].messages.ERROR_SAVE_FAILED}</Typography>
                    </>}
                    <ButtonGroup sx={{ justifyContent: 'center' }}>
                        <Button variant="outlined" color="error" onClick={handleClose}>{resourceFinal[appLanguage].words.CANCEL}</Button>
                        <Button variant="outlined" color="primary" disabled={!completeCheck || !changed} onClick={addClick}>{resourceFinal[appLanguage].words.ADD}</Button>
                    </ButtonGroup>
                </Stack>
            </Card>
        </Box>
    );
}
